/* Consented ChatGPT ad attribution. This theme asset uses Shopify's existing consent choices. */
(function () {
  if (window.__techRentalOpenAiAttribution) return;
  window.__techRentalOpenAiAttribution = true;
  const captureEnabled = document.currentScript?.dataset?.techrentalOpenaiCapture === 'true';
  const ENDPOINT = 'https://jinnapldrblkfzuypquj.supabase.co/functions/v1/openai-measurement-session';
  const STORAGE = 'techrental_openai_session_v1';
  const CLEANUP = 'techrental_openai_cleanup_v1';
  const ATTR = '__techrental_openai_session';
  let sequence = Promise.resolve();
  let disabledCleanupComplete = false;

  function allowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || navigator.globalPrivacyControl === true) return false;
    const explicit = privacy.currentVisitorConsent();
    // Strict opt-in for this new integration; a location-derived default is insufficient.
    return explicit.analytics === 'yes' && explicit.marketing === 'yes'
      && privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true
      && privacy.saleOfDataAllowed() === true;
  }
  async function request(body) {
    const response = await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'omit', body: JSON.stringify(body), signal: AbortSignal.timeout(5000) });
    if (!response.ok) throw new Error('Attribution endpoint unavailable');
    return await response.json();
  }
  async function cart(token) {
    // Double-underscore prefix makes this a private cart attribute hidden at checkout.
    // Shopify merges attributes; no cart items or prices are changed.
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
      body: JSON.stringify({ attributes: { [ATTR]: token } }), signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) throw new Error('Cart attribution unavailable');
    return await response.json();
  }
  function read() { try { return JSON.parse(localStorage.getItem(STORAGE) || 'null'); } catch { return null; } }
  function save(value) {
    try {
      if (value) localStorage.setItem(STORAGE, JSON.stringify(value));
      else localStorage.removeItem(STORAGE);
      return true;
    } catch { return false; }
  }
  function cleanupMarker(value) {
    try {
      if (value === undefined) return localStorage.getItem(CLEANUP);
      if (value) localStorage.setItem(CLEANUP, value);
      else localStorage.removeItem(CLEANUP);
    } catch { /* Storage rejection must not interrupt cart/session withdrawal. */ }
    return null;
  }
  async function cartFingerprint(value) {
    if (typeof value !== 'string' || !value) return null;
    const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)));
    return [...bytes].map(x => x.toString(16).padStart(2, '0')).join('');
  }
  async function clearCart() {
    const cleared = await cart('');
    if (!captureEnabled) cleanupMarker(await cartFingerprint(cleared.token));
  }
  async function clearUnknownCart() {
    // Private attributes cannot be read through Ajax. Inspect only the cart identity.
    // Retain its hash locally so a successful blind clear is not repeated for the same cart.
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
      credentials: 'same-origin', signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) throw new Error('Cart identity unavailable');
    const current = await response.json();
    const fingerprint = await cartFingerprint(current.token);
    if (fingerprint && cleanupMarker() === fingerprint) return;
    await clearCart();
  }
  async function retire(saved) {
    const tasks = [clearCart()];
    if (saved.token) tasks.push(request({ action: 'revoke', token: saved.token }));
    const results = await Promise.allSettled(tasks);
    return results.every(result => result.status === 'fulfilled');
  }
  async function refresh() {
    // A successfully cleaned disabled page must not erase attribution attached by a newer enabled page.
    let saved = read();
    if (!captureEnabled && disabledCleanupComplete) {
      // Still honor a later withdrawal for a session created by another enabled page.
      if (!saved?.token || (navigator.globalPrivacyControl !== true
          && (!window.Shopify?.customerPrivacy || allowed()))) return;
    }
    if (!captureEnabled || !allowed()) {
      if (saved?.token) {
        // No incoming click is retained after consent is withdrawn.
        saved = { token: saved.token, expiresAt: saved.expiresAt, revokePending: true };
        save(saved);
        if (await retire(saved)) {
          save(null);
          if (!captureEnabled) disabledCleanupComplete = true;
        }
      } else {
        if (saved || !captureEnabled) save(null);
        // Shopify retains private cart attributes independently of localStorage.
        // Collection disabled: clear any stale attribute even if its local token was lost.
        if (!captureEnabled) {
          await clearUnknownCart();
          disabledCleanupComplete = true;
        }
      }
      return;
    }
    const candidate = new URL(window.location.href).searchParams.get('oppref');
    const incoming = candidate?.trim() && candidate.length <= 4096 ? candidate : null;
    if (saved?.token && incoming && (saved.oppref !== incoming || saved.revokePending)) {
      // Persist the next click before any network call. The old token is revoke-only now.
      saved = { ...saved, revokePending: true, pendingOppref: incoming,
        pendingExpiresAt: saved.pendingOppref === incoming ? saved.pendingExpiresAt : Date.now() + 30 * 86400000 };
      save(saved);
    }
    if (saved?.token && (saved.revokePending || saved.expiresAt <= Date.now())) {
      if (!await retire(saved)) return;
      saved = saved.pendingOppref && saved.pendingExpiresAt > Date.now()
        ? { pendingOppref: saved.pendingOppref, expiresAt: saved.pendingExpiresAt } : null;
      save(saved);
    }
    if (saved && !saved.token && saved.expiresAt <= Date.now()) { saved = null; save(null); }
    if (!saved?.token) {
      const oppref = incoming || saved?.pendingOppref;
      // Only OpenAI-provided ad-click references initiate a session.
      if (!oppref) return;
      if (!allowed()) { save(null); return; }
      const expiresAt = saved?.pendingOppref === oppref ? saved.expiresAt : Date.now() + 30 * 86400000;
      if (!save({ pendingOppref: oppref, expiresAt })) return;
      const result = await request({ action: 'create', analytics: true, marketing: true, explicitConsent: true, oppref });
      if (!/^[a-f0-9]{64}$/.test(result.token || '')) return;
      saved = { token: result.token, oppref, expiresAt };
      if (!save(saved)) { await retire(saved); return; }
    }
    if (!allowed()) {
      saved = { token: saved.token, expiresAt: saved.expiresAt, revokePending: true }; save(saved);
      if (await retire(saved)) save(null);
      return;
    }
    await cart(saved.token);
    cleanupMarker(null);
  }
  function queue() { sequence = sequence.then(refresh).catch(() => {}); return sequence; }
  // A page with capture enabled invalidates any prior disabled-configuration cleanup.
  if (captureEnabled) cleanupMarker(null);
  // Keep withdrawal/cleanup available after disabling collection, without creating new sessions.
  document.addEventListener('visitorConsentCollected', queue);
  window.addEventListener('pageshow', event => { if (event.persisted) queue(); });
  if (!captureEnabled || window.Shopify?.customerPrivacy) queue();
  else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) queue(); });
  return () => sequence;
})();
